import { BookOpen, ExternalLink, ShieldQuestion } from "lucide-react";
import { useCallback } from "react";
import { commandCenterApi } from "../../api/command-center.js";
import { InlineError, LoadingTable } from "../../components/AsyncState.js";
import { SupportedScopePanel } from "../../components/SupportedScopePanel.js";
import { useApiResource } from "../../hooks/useApiResource.js";

const GUIDES = [
  {
    title: "Triage a finding",
    body: "Start with what was found, see the exact supporting evidence, then make a documented decision your team can revisit.",
    href: "/findings",
  },
  {
    title: "Read coverage honestly",
    body: "See what was assessed, what was not, and how current the answer is before relying on it.",
    href: "/coverage",
  },
  {
    title: "Prepare remediation",
    body: "Move from a verified risk to a reviewable plan. Proposals stay preview-only and never alter scanned source.",
    href: "/remediation",
  },
  {
    title: "Export an evidence folio",
    body: "Generate a recreatable A4 brief from this scan. It is not a compliance determination.",
    href: "/reports",
  },
] as const;

export function HelpPage() {
  const loadDefinitions = useCallback(
    (signal: AbortSignal) => commandCenterApi.getCountDefinitions(signal),
    [],
  );
  const definitions = useApiResource(loadDefinitions);

  return (
    <section className="page help-page" aria-labelledby="help-heading">
      <div className="page-heading">
        <div>
          <span className="page-kicker">Guided help</span>
          <h1 id="help-heading">Support and methodology</h1>
          <p>
            Plain-language guidance for making a confident, defensible decision.
          </p>
        </div>
      </div>

      <section className="help-hero" aria-label="Help introduction">
        <div>
          <span className="page-kicker">
            You do not need to know the jargon
          </span>
          <h2>Start with the decision you need to make.</h2>
        </div>
        <p>
          Each guide points to the evidence, the limits, and the next
          action—without asking you to translate a security report first.
        </p>
      </section>

      <SupportedScopePanel variant="full" />

      <section className="page-section" aria-labelledby="needs-human-heading">
        <h2 id="needs-human-heading">Why a result may need human review</h2>
        <p>
          Needs human is an intentional fail-closed truth state: the available
          evidence is not strong enough to confirm or refute the claim safely.
          Review the cited source, missing runtime context, and validator
          history before recording a decision.
        </p>
      </section>

      <section className="help-guides" aria-label="Guided workflows">
        {GUIDES.map((guide) => (
          <article className="page-section help-guide" key={guide.title}>
            <BookOpen aria-hidden="true" size={18} />
            <h2>{guide.title}</h2>
            <p>{guide.body}</p>
            <a href={guide.href}>
              Start workflow <ExternalLink aria-hidden="true" size={13} />
            </a>
          </article>
        ))}
      </section>

      <section className="page-section" aria-labelledby="glossary-heading">
        <div className="inspector-section__heading">
          <ShieldQuestion aria-hidden="true" size={18} />
          <h2 id="glossary-heading">How we count</h2>
        </div>
        <p>
          These definitions explain the numbers you see. They always name what
          is included, what is excluded, and when the result was measured.
        </p>
        {definitions.isLoading && !definitions.data ? (
          <LoadingTable columns={2} rows={5} />
        ) : null}
        {definitions.error && !definitions.data ? (
          <InlineError
            error={definitions.error}
            onRetry={definitions.refresh}
          />
        ) : null}
        {definitions.data ? (
          <dl className="glossary-list">
            {definitions.data.items.map((definition) => (
              <div key={definition.id}>
                <dt>{definition.label}</dt>
                <dd>
                  <p>{definition.definition}</p>
                  {definition.numerator ? (
                    <span>
                      <strong>Numerator:</strong> {definition.numerator}
                    </span>
                  ) : null}
                  {definition.denominator ? (
                    <span>
                      <strong>Denominator:</strong> {definition.denominator}
                    </span>
                  ) : null}
                  <details className="help-reference">
                    <summary>Technical reference</summary>
                    <code>
                      {definition.id}@{definition.version}
                    </code>
                  </details>
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
      </section>

      <section className="page-section" aria-labelledby="terms-heading">
        <h2 id="terms-heading">Evidence terms</h2>
        <dl className="glossary-list">
          <div>
            <dt>Replay matched</dt>
            <dd>
              The cited range matched the frozen source snapshot. It does not by
              itself prove exploitability.
            </dd>
          </div>
          <div>
            <dt>4V</dt>
            <dd>
              A human decision with a recorded rationale. It supplements, rather
              than erases, earlier validator provenance.
            </dd>
          </div>
          <div>
            <dt>Suppressed</dt>
            <dd>
              A policy disposition. Suppression preserves the underlying truth
              status and evidence.
            </dd>
          </div>
          <div>
            <dt>Model-generated</dt>
            <dd>
              Untrusted assistance that must retain its marker, provenance, and
              human review; it is not primary evidence.
            </dd>
          </div>
        </dl>
      </section>
    </section>
  );
}
